import { useState, useCallback } from 'react';
import type { ReactElement, ReactNode, MouseEvent, FC } from 'react';
import {
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  IconButton,
  Checkbox,
  Chip,
} from '@mui/material';
import { renderIcon } from 'src/constants/icons';
import { formatRelativeTime } from 'src/utils/relativeTime';
import { isWatchedNotification } from 'src/utils/notificationFields';
import 'src/styles/UI/Notifications.scss';

import {
  Notification,
  isMultiNotification,
  isSingleNotification,
  isBulkNotification,
} from 'src/types/Notifications';

interface NotificationRowProps {
  notification: Notification;
  onNotificationClick?: (notification: Notification) => void;
  onMarkAsRead: (notifId: number, e: MouseEvent) => void;
  onMarkAsUnread: (notifId: number, e: MouseEvent) => void;
  onSelect?: (notifId: number, selected: boolean) => void;
  isSelected?: boolean;
  showCheckbox?: boolean;
  showTypeIcon?: boolean;
  showTimestamps?: boolean;
  isDropdown?: boolean;
  newNotificationIds?: Set<number>;
}

// (Intentionally no pluralization helpers; see inline usage in renderNotificationContent)

// Notification type icon mapping (Event type icons - Green)
const NOTIF_TYPE_ICON: Record<string, ReactElement> = {
  new_email: renderIcon('notification', 'email', {
    className: 'notification-row__icon--green',
  }),
  new_documents: renderIcon('notification', 'documents', {
    className: 'notification-row__icon--green',
  }),
  new_file_owner: renderIcon('notification', 'fileOwner', {
    className: 'notification-row__icon--green',
  }),
  new_sms: renderIcon('notification', 'sms', {
    className: 'notification-row__icon--green',
  }),
  new_fax: renderIcon('notification', 'fax', {
    className: 'notification-row__icon--green',
  }),
  new_status_note: renderIcon('notification', 'statusNote', {
    className: 'notification-row__icon--green',
  }),
  event_notification: renderIcon('notification', 'info', {
    className: 'notification-row__icon--blue',
  }),
  automated_action_follow_up_required: renderIcon('notification', 'bolt', {
    style: { color: '#ed6c02' },
  }),
  applicant_not_responding: renderIcon('notification', 'bolt', {
    style: { color: '#ed6c02' },
  }),
  applicant_abandoned_certification: renderIcon('notification', 'bolt', {
    style: { color: '#ed6c02' },
  }),
  signature_assignment_failed: renderIcon('notification', 'bolt', {
    style: { color: '#ed6c02' },
  }),
  renter_document_signed_thank_you: renderIcon('notification', 'info', {
    className: 'notification-row__icon--blue',
  }),
  recert_notices_action_required: renderIcon('filter', 'recert', {
    style: { color: '#2e7d32' },
  }),
  checklist_item_cert: renderIcon('notification', 'checklist', {
    style: { color: '#2e7d32' },
  }),
  checklist_item_property: renderIcon('notification', 'checklist', {
    style: { color: '#2e7d32' },
  }),
  // ENG-4253 — account claim. Review work is a task (green checklist);
  // completion is an FYI (blue info).
  account_claim_review_requested: renderIcon('notification', 'checklist', {
    style: { color: '#2e7d32' },
  }),
  account_claim_review_overdue: renderIcon('notification', 'bolt', {
    style: { color: '#ed6c02' },
  }),
  account_claim_completed: renderIcon('notification', 'info', {
    className: 'notification-row__icon--blue',
  }),
  account_claim_locked: renderIcon('notification', 'bolt', {
    style: { color: '#ed6c02' },
  }),
};

// Helper functions
const getNotificationIcon = (notif: Notification): ReactElement => {
  if (NOTIF_TYPE_ICON[notif.notification_type]) {
    return NOTIF_TYPE_ICON[notif.notification_type];
  }

  // Recert: use Recertification icon to match filter
  if (notif.related_objects?.type === 'recert_tracking') {
    return renderIcon('filter', 'recert', {
      className: 'notification-row__icon--black',
    });
  }

  // Helper function to determine notification type with fallback
  const getNotificationType = (notification: Notification): string => {
    if (isSingleNotification(notification)) {
      return 'single';
    }
    if (isBulkNotification(notification)) {
      return 'multi'; // Both bulk and multi consolidation are "multi" for UI
    }
    if (isMultiNotification(notification)) {
      return 'multi'; // New consolidated multi-notifications
    }
    if (notification.is_property_notification) {
      return 'property';
    }
    if (notification.is_system_notification) {
      return 'system';
    }

    // Fallback to related_objects
    const relatedType = notification.related_objects?.type;
    if (relatedType === 'single_certification') {
      return 'single';
    }
    if (relatedType === 'bulk_certifications') {
      return 'multi';
    }
    if (relatedType === 'consolidated_events') {
      return 'multi';
    }
    if (relatedType === 'property') {
      return 'property';
    }
    if (relatedType === 'system') {
      return 'system';
    }

    // Default to single for legacy notifications
    return 'single';
  };

  const notificationType = getNotificationType(notif);

  if (notificationType === 'single') {
    return renderIcon('filter', 'single', {
      className: 'notification-row__icon--black',
    });
  }
  if (notificationType === 'multi') {
    return renderIcon('filter', 'bulk', {
      className: 'notification-row__icon--black',
    });
  }
  if (notificationType === 'property') {
    // For property notifications, show single/multi icon instead of property icon
    const propertyNotificationType = getNotificationType(notif);
    if (propertyNotificationType === 'single') {
      return renderIcon('filter', 'single', {
        className: 'notification-row__icon--black',
      });
    }
    if (propertyNotificationType === 'bulk') {
      return renderIcon('filter', 'bulk', {
        className: 'notification-row__icon--black',
      });
    }
  }
  if (notificationType === 'system') {
    return renderIcon('notification', 'system', {
      className: 'notification-row__icon--blue',
    });
  }
  return renderIcon('filter', 'all');
};

const getNotificationTypeIcon = (
  notif: Notification
): { icon: ReactElement; tooltip: string } => {
  // Recert: Recertification icon and dedicated tooltip
  if (notif.related_objects?.type === 'recert_tracking') {
    return {
      icon: renderIcon('filter', 'recert', {
        className: 'notification-row__icon--black',
      }),
      tooltip: 'Upcoming Recertification - Action required',
    };
  }
  // Helper function to determine notification type with fallback
  const getNotificationType = (notification: Notification): string => {
    if (isSingleNotification(notification)) {
      return 'single';
    }
    if (isBulkNotification(notification)) {
      return 'multi'; // Both bulk and multi consolidation are "multi" for UI
    }
    if (isMultiNotification(notification)) {
      return 'multi'; // New consolidated multi-notifications
    }
    if (notification.is_property_notification) {
      return 'property';
    }
    if (notification.is_system_notification) {
      return 'system';
    }

    // Fallback to related_objects
    const relatedType = notification.related_objects?.type;
    if (relatedType === 'single_certification') {
      return 'single';
    }
    if (relatedType === 'bulk_certifications') {
      return 'multi';
    }
    if (relatedType === 'consolidated_events') {
      return 'multi';
    }
    if (relatedType === 'property') {
      return 'property';
    }
    if (relatedType === 'system') {
      return 'system';
    }

    // Default to single for legacy notifications
    return 'single';
  };

  const notificationType = getNotificationType(notif);

  if (notificationType === 'multi') {
    return {
      icon: renderIcon('filter', 'bulk', {
        className: 'notification-row__icon--black',
      }),
      tooltip:
        'Consolidated Notification - Multiple events of the same type grouped together within a 30-minute window',
    };
  }
  if (notificationType === 'property') {
    // For property notifications, show single/multi icon instead of property icon
    const propertyNotificationType = getNotificationType(notif);
    if (propertyNotificationType === 'single') {
      return {
        icon: renderIcon('filter', 'single', {
          className: 'notification-row__icon--black',
        }),
        tooltip:
          'Single Certification - Individual certification events and updates',
      };
    }
    if (propertyNotificationType === 'bulk') {
      return {
        icon: renderIcon('filter', 'bulk', {
          className: 'notification-row__icon--black',
        }),
        tooltip:
          'Bulk Certification Actions - Multiple certifications affected by a single operation',
      };
    }
  }
  if (notificationType === 'system') {
    return {
      icon: renderIcon('notification', 'system', {
        className: 'notification-row__icon--blue',
      }),
      tooltip: 'System Notification - System-wide updates and announcements',
    };
  }
  return {
    icon: renderIcon('filter', 'single', {
      className: 'notification-row__icon--black',
    }),
    tooltip:
      'Single Certification - Individual certification events and updates',
  };
};

// Render multi-notification counter badge (works for both bulk certifications and consolidated events)
const renderMultiIndicator = (notification: Notification): ReactNode => {
  // Handle consolidated multi-notifications (new consolidation feature)
  if (isMultiNotification(notification)) {
    const count = notification.related_objects?.consolidated_count || 0;
    if (count <= 1) {
      return null;
    }

    return (
      <Chip
        size="small"
        label={count}
        sx={{
          position: 'absolute',
          top: '-6px',
          right: '-8px',
          minWidth: '24px',
          width: 'auto',
          height: '16px',
          fontWeight: 'bold',
          fontSize: '10px',
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          '& .MuiChip-label': {
            padding: '0 6px',
            fontSize: '10px',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
            overflow: 'visible',
          },
        }}
      />
    );
  }

  // Handle bulk certifications (file owner assignments)
  if (isBulkNotification(notification)) {
    const count = notification.related_objects?.total_count || 0;
    if (count <= 1) {
      return null;
    }

    return (
      <Chip
        size="small"
        label={count}
        sx={{
          position: 'absolute',
          top: '-6px',
          right: '-8px',
          minWidth: '24px',
          width: 'auto',
          height: '16px',
          fontWeight: 'bold',
          fontSize: '10px',
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          '& .MuiChip-label': {
            padding: '0 6px',
            fontSize: '10px',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
            overflow: 'visible',
          },
        }}
      />
    );
  }

  return null;
};

const renderNotificationContent = (notif: Notification): ReactNode => {
  // Helper function to determine if notification is bulk
  const isBulkNotification = (notification: Notification): boolean => {
    return (
      notification.is_bulk_notification ||
      notification.related_objects?.type === 'bulk_certifications'
    );
  };

  if (isBulkNotification(notif)) {
    const count = (notif.related_objects as any)?.total_count ?? 0;
    const actionType = (notif.related_objects as any)?.action_type ?? '';
    const certCountMsg = `${count} certification${count > 1 ? 's' : ''}`;

    const sourceLabelFromData = (): string | null => {
      const sourceType = notif.related_objects?.source_type;
      if (sourceType === 'rent_roll_file') {
        return 'rent roll upload';
      }
      if (sourceType === 'bulk_communication') {
        return 'bulk communications';
      }
      return null;
    };

    const assignmentMessage = (source?: string | null): string => {
      const watched = isWatchedNotification(notif);
      const suffix = source ? ` from ${source}` : '';
      return watched
        ? `File owner changed for ${certCountMsg} you're watching${suffix}`
        : `You were assigned ${certCountMsg} as file owner${suffix}`;
    };

    const fallback = (): string => {
      const title = actionType
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (l) => l.toUpperCase());
      return `${title} - ${certCountMsg}`;
    };

    const source = sourceLabelFromData();

    switch (actionType) {
      case 'bulk_file_owner_assignment':
      case 'file_owner_change':
        return assignmentMessage(source);
      case 'bulk_status_change':
        return `${certCountMsg} had status changes`;
      case 'bulk_property_transfer':
        return `${certCountMsg} were transferred`;
      case 'rent_roll_upload':
        return assignmentMessage('rent roll upload');
      case 'bulk_communication':
        return assignmentMessage('bulk communications');
      default:
        return fallback();
    }
  } else if (notif.is_property_notification) {
    return notif.content || 'Property notification';
  } else if (notif.is_system_notification) {
    return notif.content || 'System notification';
  } else {
    return notif.content;
  }
};

// --- UI helpers -------------------------------------------------------------
const getRowClassName = (
  notif: Notification,
  isDropdown: boolean,
  newNotificationIds: Set<number>
): string => {
  const classes: string[] = ['notification-row__container'];
  if (!notif.is_read) {
    classes.push('notification-row__container--unread');
  }
  if (isWatchedNotification(notif)) {
    classes.push('notification-row__container--watched');
  }
  if (isDropdown) {
    classes.push('notification-row__container--dropdown');
  }
  if (newNotificationIds.has(notif.id)) {
    classes.push('notification-row__container--new');
  }
  return classes.join(' ');
};

const renderSourceIcon = (notif: Notification): ReactElement => {
  if (notif.is_property_notification) {
    return renderIcon('notification', 'property', {
      className: 'notification-row__icon--blue',
    });
  }
  if (isWatchedNotification(notif)) {
    return renderIcon('notification', 'watching', {
      className: 'notification-row__icon--blue',
    });
  }
  return renderIcon('header', 'fileOwner', {
    className: 'notification-row__icon--blue',
  });
};

const renderAttachmentIndicator = (notification: Notification): ReactNode => {
  // Check if notification has attachment information
  const hasAttachments =
    notification.related_objects?.attachments?.has_attachments;

  if (!hasAttachments) {
    return null;
  }

  return (
    <Box
      className="notification-row__attachment-indicator"
      sx={{
        position: 'absolute',
        top: '-4px',
        right: '-4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.paper',
        borderRadius: '50%',
        width: '16px',
        height: '16px',
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}
    >
      {renderIcon('notification', 'attachment', {
        className: 'notification-row__attachment-icon',
        sx: {
          fontSize: '12px',
          color: 'text.secondary',
        },
      })}
    </Box>
  );
};

const renderSecondaryContentLeft = (notif: Notification): ReactNode => {
  if (notif.is_bulk_notification) {
    return (
      <Typography
        variant="caption"
        component="span"
        className="notification-row__bulk-instruction"
      >
        Click for list of certifications
      </Typography>
    );
  }

  if (notif.is_property_notification) {
    const propertyName =
      notif.property?.name || notif.properties?.[0]?.name || '';
    return propertyName ? (
      <Typography
        variant="caption"
        component="span"
        className="notification-row__property-name"
      >
        {propertyName}
      </Typography>
    ) : null;
  }

  const householdName =
    notif.certification?.household_name ||
    notif.certifications?.[0]?.household_name ||
    notif.certification?.household?.name ||
    notif.certifications?.[0]?.household?.name ||
    '';
  const propertyName =
    notif.certification?.property?.name ||
    notif.certifications?.[0]?.property?.name ||
    '';

  return (
    <>
      {householdName && (
        <Typography
          variant="caption"
          component="span"
          className="notification-row__household-name"
        >
          {householdName}
        </Typography>
      )}
      {propertyName && (
        <Typography
          variant="caption"
          component="span"
          className="notification-row__property-name-single"
        >
          {propertyName}
        </Typography>
      )}
    </>
  );
};

const renderActionButtons = (
  notif: Notification,
  onMarkAsRead: (id: number, e: MouseEvent) => void,
  onMarkAsUnread: (id: number, e: MouseEvent) => void
): ReactNode => {
  if (notif.is_read) {
    return (
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onMarkAsUnread(notif.id, e);
        }}
        className="notification-row__action-button"
      >
        {renderIcon('filter', 'read', {
          style: { color: '#2e7d32' },
          className: 'notification-row__action-icon--read',
        })}
      </IconButton>
    );
  }

  return (
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        onMarkAsRead(notif.id, e);
      }}
      className="notification-row__action-button"
    >
      {renderIcon('filter', 'unread', {
        style: { color: '#2e7d32' },
        className: 'notification-row__action-icon--unread',
      })}
    </IconButton>
  );
};

const NotificationRow: FC<NotificationRowProps> = ({
  notification,
  onNotificationClick,
  onMarkAsRead,
  onMarkAsUnread,
  onSelect,
  isSelected = false,
  showCheckbox = false,
  showTypeIcon = true,
  showTimestamps = true,
  isDropdown = false,
  newNotificationIds = new Set(),
}) => {
  const [_isHovered, setIsHovered] = useState<boolean>(false);

  // Single handler to prevent checkbox interactions from triggering row click
  const stopPropagation = useCallback((e: MouseEvent) => {
    e.stopPropagation();
  }, []);

  const listItem = (
    <ListItem
      onClick={
        onNotificationClick
          ? () => onNotificationClick(notification)
          : undefined
      }
      sx={!onNotificationClick ? { cursor: 'default' } : undefined}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={getRowClassName(notification, isDropdown, newNotificationIds)}
    >
      {showCheckbox && (
        <Box
          onClick={stopPropagation}
          onMouseDown={stopPropagation}
          onMouseUp={stopPropagation}
        >
          <Checkbox
            checked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              if (onSelect) {
                onSelect(notification.id, e.target.checked);
              }
            }}
            onClick={stopPropagation}
            className="notification-row__checkbox"
          />
        </Box>
      )}
      <ListItemIcon className="notification-row__icon-container">
        <Box className="notification-row__icon-stack">
          {renderSourceIcon(notification)}
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            {getNotificationIcon(notification)}
            {renderAttachmentIndicator(notification)}
          </Box>
        </Box>
      </ListItemIcon>
      <ListItemText
        primary={
          <Box className="notification-row__content">
            <Typography
              variant="body2"
              component="span"
              className={`notification-row__content-text ${
                !notification.is_read
                  ? 'notification-row__content-text--unread'
                  : ''
              }`}
              color={notification.is_read ? 'text.primary' : 'primary.main'}
              noWrap={isDropdown}
            >
              {renderNotificationContent(notification)}
            </Typography>
            <Box className="notification-row__content-actions">
              {showTypeIcon && (
                <Box
                  className="notification-row__type-icon-container"
                  sx={{ position: 'relative' }}
                >
                  {getNotificationTypeIcon(notification).icon}
                  {renderMultiIndicator(notification)}
                </Box>
              )}
              {renderActionButtons(notification, onMarkAsRead, onMarkAsUnread)}
            </Box>
          </Box>
        }
        secondaryTypographyProps={{ component: 'div' }}
        secondary={
          <Box className="notification-row__secondary-content">
            <Box className="notification-row__secondary-left">
              {renderSecondaryContentLeft(notification)}
            </Box>
            {showTimestamps && (
              <Box className="notification-row__secondary-right">
                <Typography
                  variant="caption"
                  component="span"
                  className="notification-row__timestamp"
                >
                  {formatRelativeTime(notification.created).displayText}
                </Typography>
                {notification.is_read && notification.read_at && (
                  <Typography
                    variant="caption"
                    component="span"
                    className="notification-row__timestamp notification-row__timestamp--read"
                  >
                    {formatRelativeTime(notification.read_at).displayText}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        }
      />
    </ListItem>
  );

  return listItem;
};

export default NotificationRow;
